
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Shield, User, Settings, RotateCcw, Square, XCircle, Siren } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import Tradebook from "@/components/tradebook/Tradebook";
import SwipeGuard from "@/components/gestures/SwipeGuard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import CircularGauge270 from "@/components/CircularGauge270";
import VerticalMeter from "@/components/VerticalMeter";
import StreakMeter from "@/components/StreakMeter";
import { CooldownStopwatch, LossStreakCells } from "@/components/CooldownLossWidgets";
import { subscribeToPush } from "@/utils/pushSubscribe";


const BACKEND_URL = "https://api.bohoapp.com";
const API = `${BACKEND_URL}/api`;


const Dashboard = () => {
  const [theme, setTheme] = useState(() => {
  return localStorage.getItem("theme") || "dark";
});

  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configForm, setConfigForm] = useState({});
  const [maxLossMode, setMaxLossMode] = useState("abs"); // "abs" | "pct"
const [maxProfitMode, setMaxProfitMode] = useState("abs");
  const [justSaved, setJustSaved] = useState(false);
  const [showKillSwitch, setShowKillSwitch] = useState(false);
const [killText, setKillText] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);
  const [streakHistory, setStreakHistory] = useState([]);
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);
  const lastScrollY = useRef(0);
const [showEmergency, setShowEmergency] = useState(false);
  const [fabPos, setFabPos] = useState({ x: 24, y: 24 });
  const [fabIcon, setFabIcon] = useState("app"); // "app" | "siren"
const [isSpinning, setIsSpinning] = useState(false);






  const [showTripReason, setShowTripReason] = useState(false);
  const [activeSection, setActiveSection] = useState("status");
    // ---------------- COOLDOWN TIME CALCULATION ----------------
  const now = Date.now();

  const cooldownRemainingMin =
    status?.cooldown_active && status?.cooldown_until
      ? Math.max(
          0,
          Math.ceil((status.cooldown_until - now) / 60000)
        )
      : 0;
  const confirmCancelOrders = () => {
  setConfirmAction({
    title: "Cancel all pending orders?",
    message: "This will cancel all open orders.",
    color: "text-yellow-500",
    btnClass: "bg-yellow-600 hover:bg-yellow-700 text-white",
    onConfirm: handleCancelAll, // 👈 real action
  });
};
const confirmKillPositions = () => {
  setConfirmAction({
    title: "Square off all positions?",
    message: "This will immediately square off all open positions at market price.",
    color: "text-red-500",
    btnClass: "bg-red-600 hover:bg-red-700 text-white",
    onConfirm: handleKill,
  });
};

  const fetchData = async () => {
    console.log("fetchData called", new Date().toLocaleTimeString());

    try {
      const [configRes, statusRes, logsRes, tradesRes] = await Promise.all([
        axios.get(`${API}/risk-config`),
        axios.get(`${API}/risk-status`),
        axios.get(`${API}/logs?limit=50`),
        axios.get(`${API}/trades?limit=100`),

      ]);

      
     setStatus(statusRes.data.state);
const mtm = statusRes.data.state.mtm_log;
setStreakHistory(buildStreakFromMTM(mtm));

const cfg = configRes.data.config;
setConfig(cfg);

// 🔥 PATCH #3: prevent overwrite immediately after save
if (justSaved) {
  setJustSaved(false);
}

if (!editing) {
  setConfigForm({
    capital_day_915: cfg.capital_day_915 ?? "",
    max_loss_abs: cfg.max_loss_abs ?? "",
    max_profit_abs: cfg.max_profit_abs ?? "",
    min_loss_to_count: cfg.min_loss_to_count ?? "",
    trail_step_profit: cfg.trail_step_profit ?? "",
    cooldown_min: cfg.cooldown_min ?? "",
    max_consecutive_losses: cfg.max_consecutive_losses ?? "",
    cooldown_on_profit: cfg.cooldown_on_profit ?? "",
    max_trades_per_day: cfg.max_trades_per_day ?? "",
    side_lock: cfg.side_lock ?? ""
  });
}

     // Logs: normalize + timestamp fix
const rawLogs =
  Array.isArray(logsRes.data?.logs)
    ? logsRes.data.logs
    : Array.isArray(logsRes.data)
    ? logsRes.data
    : [];

const normalizedLogs = rawLogs.map((l) => {
  const ts = Number(
    l.timestamp ??
    l.time ??
    l.ts ??
    l.created_at ??
    0
  );

  let message = l.message;

  // 🔥 build message if missing
  if (!message) {
    if (l.type === "MTM") {
      message = `Live MTM: ₹${Math.round(l.value).toLocaleString()}`;
    } else if (l.type === "TRIP") {
      message = "Trading day tripped";
    } else if (l.type === "TRADE") {
      message = "Trade executed";
    } else {
      message = JSON.stringify(l);
    }
  }

  return {
    ...l,
    message,
    _ts: ts > 0 ? ts : null,
  };
});

setLogs(normalizedLogs);


setLogs(normalizedLogs);

    // ===================== TRADE EXTRACTION (FINAL) =====================
const data = tradesRes.data;

let rawTrades =
  Array.isArray(data) ? data :
  Array.isArray(data?.trades) ? data.trades :
  Array.isArray(data?.state?.trades) ? data.state.trades :
  Array.isArray(data?.data?.trades) ? data.data.trades :
  data?.trades && typeof data.trades === "object" ? Object.values(data.trades) :
  data?.state?.trades && typeof data.state.trades === "object" ? Object.values(data.state.trades) :
  [];

const normalizedTrades = rawTrades.map(t => {
  const price =
    t.price ??
    t.average_price ??
    t.raw?.average_price ??
    t.raw?.price ??
    0;

  return {
    ...t,
    tradingsymbol: t.tradingsymbol ?? t.raw?.tradingsymbol,
    side: t.side ?? t.transaction_type ?? t.raw?.transaction_type,
    qty: Number(t.qty ?? t.quantity ?? t.raw?.quantity ?? 0),
    price: Number(price),
    ts: Number(t.ts ?? t.timestamp ?? t.raw?._ts ?? 0),
    iso_date: t.iso_date ?? t.raw?.exchange_timestamp ?? null,
  };
});

const sortedTrades = normalizedTrades
  .filter(t => t.ts > 0)
  .sort((a, b) => b.ts - a.ts);

setTrades(sortedTrades);
// ================================================================


      
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch data");
      setLoading(false);
    }
  };
 const [editing, setEditing] = useState(false);
  const configCardRef = useRef(null);
useEffect(() => {
  const root = document.documentElement;

  // remove all theme classes
  root.classList.remove("dark", "amoled");

  const isMobile = window.matchMedia("(pointer: coarse)").matches;
  const isHighDPI = window.devicePixelRatio >= 2;
  const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;

  // heuristic: OLED devices are almost always mobile + high DPI
  const isOLEDDevice = isMobile && isHighDPI && prefersDark;

  if (theme === "dark") {
    if (isOLEDDevice) {
      root.classList.add("amoled"); // 🔥 auto AMOLED
    } else {
      root.classList.add("dark");   // normal dark
    }
  }

  localStorage.setItem("theme", theme);
}, [theme]);

  const tabRefs = useRef({});
useEffect(() => {
  const el = tabRefs.current[activeSection];
  if (!el) return;

  el.scrollIntoView({
    behavior: "smooth",
    inline: "center",
    block: "nearest",
  });
}, [activeSection]);
  
  useEffect(() => {
  const handleScroll = () => {
    const currentY = window.scrollY;

    if (currentY > lastScrollY.current + 10) {
      // scrolling down → collapse header
      setIsHeaderCollapsed(true);
    } else if (currentY < lastScrollY.current - 10) {
      // scrolling up → expand header
      setIsHeaderCollapsed(false);
    }

    lastScrollY.current = currentY;
  };

  window.addEventListener("scroll", handleScroll, { passive: true });
  return () => window.removeEventListener("scroll", handleScroll);
}, []);
  useEffect(() => {
  if (showEmergency) return; // stop animation when menu is open

  const interval = setInterval(() => {
    setIsSpinning(true);

    // swap icon at mid-rotation
    setTimeout(() => {
      setFabIcon(prev => (prev === "app" ? "siren" : "app"));
    }, 350);

    setTimeout(() => {
      setIsSpinning(false);
    }, 700);
  }, 4500); // every ~4.5s

  return () => clearInterval(interval);
}, [showEmergency]);

  
useEffect(() => {
  subscribeToPush().catch(console.error);
}, []);



  useEffect(() => {
  if (!editing) {
    fetchData();
  }

  const interval = setInterval(() => {
    if (!editing) {
      fetchData();
    }
  }, 30000);

  return () => clearInterval(interval);
}, [editing]);

  const handleLoginRedirect = () => {
  let key = localStorage.getItem("admin_key");

  if (!key) {
    key = prompt("Enter Admin Key:");
    if (!key) {
      toast.error("Login cancelled");
      return;
    }
    localStorage.setItem("admin_key", key);
    toast.success("Admin key saved");
  }

  window.location.href = "https://api.bohoapp.com";
};



const handleUpdateConfig = async () => {
  try {
    // FIX 1: stop auto-refresh overwriting user input
    setEditing(false);

    // FIX 2: build safe payload
  const payload = {};

Object.entries(configForm).forEach(([key, value]) => {
  if (value === "" || value === null || value === undefined) return;

  // ✅ BOOLEAN FIELDS (ONLY THESE TWO)
  if (key === "cooldown_on_profit" || key === "side_lock") {
    payload[key] = value === true || value === "true";
    return;
  }

  // 🔒 ALL OTHER FIELDS MUST BE NUMERIC
  const num = Number(value);
  if (Number.isNaN(num)) return;

  // 🔒 HARD CAP: max consecutive losses ≤ 10
  if (key === "max_consecutive_losses") {
    payload[key] = Math.min(10, Math.max(0, num));
    return;
  }

  payload[key] = num;
});

        // -------- % → ₹ conversion (PATCH #5) --------
    const capital = Number(configForm.capital_day_915 || 0);

    if (maxLossMode === "pct" && capital > 0) {
      payload.max_loss_abs = Math.round(
        (Number(configForm.max_loss_abs) / 100) * capital
      );
    }

    if (maxProfitMode === "pct" && capital > 0) {
      payload.max_profit_abs = Math.round(
        (Number(configForm.max_profit_abs) / 100) * capital
      );
    }


    // FIX 3: send only valid fields
await axios.put(`${API}/risk-config`, payload, {
  headers: {
    "Content-Type": "application/json",
    "x-admin-key": localStorage.getItem("admin_key"),
  },
});
setJustSaved(true);
await fetchData();
setMaxLossMode("abs");
setMaxProfitMode("abs");
toast.success("Configuration updated");

} catch (err) {
  console.error("Error updating configuration:", err);
  toast.error("Failed to update configuration");
}
};




  const handleResetDay = async () => {
    try {
      await axios.post(`${API}/reset-day`, {}, {
        headers: { "x-admin-key": localStorage.getItem("admin_key") }
      });
      toast.success("Day reset successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to reset day");
    }
  };

  const handleCancelAll = async () => {

  const key = localStorage.getItem("admin_key");
  if (!key) return toast.error("Please login first");

  try {
    await axios.post(`${API}/cancel`, {}, {
      headers: { "x-admin-key": key }
    });
    toast.success("All pending orders cancelled");
    fetchData();
  } catch {
    toast.error("Failed to cancel orders");
  }
};

const handleKill = async () => {

  const key = localStorage.getItem("admin_key");
  if (!key) return toast.error("Please login first");

  try {
    await axios.post(`${API}/kill`, {}, {
      headers: { "x-admin-key": key }
    });
    toast.success("All positions squared off");
    fetchData();
  } catch {
    toast.error("Failed to Square Off Positions");
  }
};
const handleKillSwitch = async () => {
  const key = localStorage.getItem("admin_key");
  if (!key) return toast.error("Please login first");

  if (killText !== "KILL NOW") {
    toast.error("Type KILL NOW to confirm");
    return;
  }

  try {
    await axios.post(
      `${API}/admin/trip`,
      {},
      { headers: { "x-admin-key": key } }
    );

    toast.success("Kill Switch activated. Trading locked for today.");
    setShowKillSwitch(false);
    setKillText("");
    fetchData();
  } catch {
    toast.error("Failed to activate Kill Switch");
  }
};
  const confirmResetDay = () => {
  // 🔒 Market hours guard
  if (!isResetDayAllowedIST()) {
    toast.info("Reset available after 3:30 PM IST");
    return;
  }

  setConfirmAction({
    title: "Reset trading day?",
    message: "This will reset all daily limits, counters, and cooldowns.",
    color: "text-blue-500",
    btnClass: "bg-blue-600 hover:bg-blue-700 text-white",
    onConfirm: handleResetDay,
  });
};

  function isResetDayAllowedIST() {
  const istNow = new Date(
    new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" })
  );

  const mins = istNow.getHours() * 60 + istNow.getMinutes();

  // Allowed before 9:00 AM OR after 3:30 PM IST
  return mins < 540 || mins >= 930;
}
const IST_OFFSET = 5.5 * 60 * 60 * 1000;


function buildStreakFromMTM(logs) {
  if (!logs || logs.length === 0) return [];

  const streak = [];
  let prev = 0;

  for (let i = 0; i < logs.length; i++) {
    const curr = logs[i].realised;
    const delta = Math.round(curr - prev); // 🔥 FIX

    if (delta > 0) {
      streak.push({ type: "profit", amount: delta });
    } else if (delta < 0) {
      streak.push({ type: "loss", amount: delta });
    }

    prev = curr;
  }

  return streak.reverse();
}
 


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 select-none mx-auto mb-4"></div>
          <p className="text-zinc-400 text-lg">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
  <div className="min-h-screen select-none bg-background text-foreground overflow-x-hidden">
    
<header className="fixed top-0 left-0 right-0 z-50 bg-background">




  {/* TOP ROW (collapsible) */}
    <div
  className={`
    container mx-auto px-6 h-[56px]
    transition-transform duration-300 ease-out
    ${isHeaderCollapsed ? "-translate-y-full" : "translate-y-0"}
  `}
>



    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        {!isHeaderCollapsed && (
  <button
    onClick={() => setShowEmergency(v => !v)}
    className="
      min-w-12 min-h-12 rounded-full
      bg-background text-white
      flex items-center justify-center
    "
    aria-label="Emergency Controls"
  >
    <img
  src={
    theme === "light"
      ? "/boho-mark-black.png"
      : "/boho-mark-orange.png"
  }
  alt="Boho"
  className="h-12 w-12 select-none"
/>
  </button>
)}

  <div>
      <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground">
  BOHO Risk Engine
</h1>
</div>
      </div>

      <div className="flex items-center gap-3">
  <Button
    variant="outline"
    size="icon"
    className="border-white/20 text-zinc-500 dark:text-zinc-300"
    onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
    title="Toggle theme"
  >
    {theme === "dark" ? "☀️" : "🌙"}
  </Button>

  <Button
    variant="outline"
    size="sm"
    className="border-orange-500 text-orange-500 hover:bg-orange-500/10"
    onClick={handleLoginRedirect}
  >
    <User className="h-4 w-4 mr-2" />
    Login
  </Button>
</div>
    </div>
  </div>

  {/* BOTTOM ROW (tabs – always visible) */}
<nav
  className={`
    fixed left-0 right-0 z-50
    bg-background/95 backdrop-blur-xl
    border-b border-border
    transition-top duration-300 ease-out
    touch-pan-x overscroll-y-none
    ${isHeaderCollapsed ? "top-0" : "top-[56px]"}
  `}
>
  {/* INNER ALIGNED CONTAINER */}
 <div
  className="
    container mx-auto
    px-6
    flex gap-8
    h-[48px]
    pt-4
    text-sm font-medium
    overflow-x-auto whitespace-nowrap
    scrollbar-hide
    snap-x snap-mandatory
    scroll-px-6
  "
>

    {[
      ["status", "Status"],
      ["config", "Configuration"],
      ["tradebook", "Tradebook"],
      ["logs", "Logs"],
      ["journal", "Journal"],
    ].map(([key, label]) => (
      <button
        key={key}
        ref={(el) => {
          if (el) tabRefs.current[key] = el;
        }}
        onClick={() => setActiveSection(key)}
        className={`
          pb-3 snap-start shrink-0 relative
          transition-all duration-200

          ${activeSection === key
            ? "text-orange-500"
            : "text-muted-foreground"
          }
        `}
      >
        {label}

        {activeSection === key && (
          <span
            className="
              absolute left-2 right-2 -bottom-0
              h-[6px]
              bg-orange-500
              rounded-full
              transition-all duration-300 ease-out
            "
          />
        )}
      </button>
    ))}
  </div>
</nav>

</header>
<SwipeGuard
  activeSection={activeSection}
  setActiveSection={setActiveSection}
>
 <div
  className="container mx-auto px-6 pt-[120px] pb-8 text-zinc-800 dark:text-zinc-200"
 >




       {activeSection === "status" && (
         <>
         <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8 mt-10">
<div className="lg:col-span-2 flex justify-center items-center
      bg-card/60 text-card-foreground
backdrop-blur-xl rounded-3xl shadow-2xl p-8
border border-border
transition-all duration-300
hover:shadow-orange-600/40
active:shadow-orange-600/40
focus-within:shadow-orange-600/40 glass-card">


            <CircularGauge270
              realised={status?.realised || 0}
  unrealised={status?.unrealised || 0}
  total={status?.total_pnl || 0}
  peakProfit={status?.peak_profit || 0}
  activeLossFloor={status?.active_loss_floor || 0}
  maxLoss={config?.max_loss_abs || 1}
  maxProfit={config?.max_profit_abs || 1}
              size={380}
            />
          </div>
       <div className="glass-card rounded-3xl shadow-2xl p-6
border border-border
transition-all duration-300
hover:shadow-orange-600/40
active:shadow-orange-600/40
focus-within:shadow-orange-600/40
 flex justify-center ">
  <div className="flex flex-col sm:flex-col gap-6 sm:gap-10 items-center">
    <CooldownStopwatch
      remainingMin={cooldownRemainingMin}
      maxMin={config?.cooldown_min || 0}
 cooldownUntil={status?.cooldown_until}
    />

    <LossStreakCells
      losses={status?.consecutive_losses || 0}
      maxLosses={config?.max_consecutive_losses || 0}
    />
  </div>
</div>
</div>

     
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="
  glass-card rounded-3xl shadow-2xl p-6
  border border-border
  transition-all duration-300
  hover:shadow-orange-600/40
  active:shadow-orange-600/40
  focus-within:shadow-orange-600/40
">
            <h3 className="text-lg font-semibold mb-6 text-foreground">Performance Metrics</h3>
            <div className="flex justify-around items-end">
              <VerticalMeter
                value={status?.trades_today || 0}
                max={config?.max_trades_per_day || 10}
                label="No. of Trades"
                warningThreshold={80}
                size={180}
              />
              <VerticalMeter
                value={5}
                max={10}
                label="Win Streak"
                warningThreshold={90}
                size={180}
              />
              <VerticalMeter
                value={2}
                max={10}
                label="Impulsiveness"
                warningThreshold={60}
                size={180}
              />
            </div>
          </div>
          
          <StreakMeter trades={streakHistory} maxDisplay={20} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="hidden md:block glass-card rounded-3xl shadow-2xl p-6
border border-border
transition-all duration-300
hover:shadow-orange-600/40
active:shadow-orange-600/40
focus-within:shadow-orange-600/40

">
            <h3 className="text-lg font-semibold mb-4 text-muted-foreground">Key Metrics</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className=" text-center p-4 rounded-xl bg-card border border-border backdrop-blur-sm">
                <div className="text-xs text-muted-foreground mb-1">Peak Profit</div>
                <div className="text-lg font-bold text-green-600">₹{status?.peak_profit?.toLocaleString() || '0'}</div>
              </div>
              <div className=" text-center p-4 rounded-xl bg-card border border-border backdrop-blur-sm">
                <div className="text-xs text-muted-foreground mb-1">Active Loss Floor</div>
                <div className="text-lg font-bold text-blue-600">₹{status?.active_loss_floor?.toLocaleString() || '0'}</div>
              </div>
              <div className=" text-center p-4 rounded-xl bg-card border border-border backdrop-blur-sm">
                <div className="text-xs text-muted-foreground mb-1">Remaining to Max Loss</div>
                <div className="text-lg font-bold text-red-600">₹{status?.remaining_to_max_loss?.toLocaleString() || '0'}
              </div>

              </div>
              <div className=" text-center p-4 rounded-xl bg-card border border-border backdrop-blur-sm ">
                <div className="text-xs text-muted-foreground mb-1">Last Trade Time</div>
                <div className="text-sm font-bold text-purple-600">
                  {status?.last_trade_time 
                    ? new Date(status.last_trade_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
                    : 'N/A'}
                </div>
              </div>
            </div>
          </div>



        </div>
 </>
)}


       

          {activeSection === "config" && (
  <div className="mt-10" ref={configCardRef}>
            <Card className="bg-card/60 text-card-foreground backdrop-blur-xl border border-border
shadow-2xl
transition-all duration-300
hover:shadow-orange-600/40
active:shadow-orange-600/40
focus-within:shadow-orange-600/40 glass-card

">


              <CardHeader>
                <CardTitle>Advanced Risk Configuration</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Set Capital (₹)</Label>
                    <Input
                      type="number"
                      value={configForm.capital_day_915 || ''}
                      onChange={(e) => setConfigForm({...configForm, capital_day_915: e.target.value})}
                      data-testid="capital-input"
                        onFocus={() => setEditing(true)}
                       
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Min Loss to Count (₹)</Label>
                    <Input
                      type="number"
                      value={configForm.min_loss_to_count || ''}
                      onChange={(e) => setConfigForm({...configForm, min_loss_to_count: e.target.value})}
                      data-testid="min-loss-input"
                        onFocus={() => setEditing(true)}
                       
                    />
                          </div>
                  <div className="space-y-2">
  <Label>Daily Max Loss</Label>

  <div className="flex gap-2">
    <Select
  value={maxLossMode}
  onValueChange={(v) => {
    setMaxLossMode(v);

    // 🔥 clear input when switching to %
    if (v === "pct") {
      setConfigForm({
        ...configForm,
        max_loss_abs: "",
      });
    }
  }}
>

      <SelectTrigger className="w-20">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="abs">₹</SelectItem>
        <SelectItem value="pct">%</SelectItem>
      </SelectContent>
    </Select>

    <Input
      type="number"
      inputMode="numeric"
      value={configForm.max_loss_abs || ''}
      onWheel={(e) => e.currentTarget.blur()}   // 🔥 disable scroll
      onChange={(e) =>
        setConfigForm({ ...configForm, max_loss_abs: e.target.value })
      }
      onFocus={() => setEditing(true)}
    />
  </div>
</div>
{/* Daily Max Profit */}
<div className="space-y-2">
  <Label>Daily Max Profit</Label>

  <div className="flex gap-2">
<Select
  value={maxProfitMode}
  onValueChange={(v) => {
    setMaxProfitMode(v);

    // 🔥 clear input when switching to %
    if (v === "pct") {
      setConfigForm({
        ...configForm,
        max_profit_abs: "",
      });
    }
  }}
>

      <SelectTrigger className="w-20">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="abs">₹</SelectItem>
        <SelectItem value="pct">%</SelectItem>
      </SelectContent>
    </Select>

    <Input
      type="number"
      inputMode="numeric"
      value={configForm.max_profit_abs || ""}
      onWheel={(e) => e.currentTarget.blur()}
      onFocus={() => setEditing(true)}
      onChange={(e) =>
        setConfigForm({
          ...configForm,
          max_profit_abs: e.target.value,
        })
      }
    />
  </div>
</div>

{/* Trail Step Profit */}
<div className="space-y-2">
  <Label>Trail Step Profit (₹)</Label>

  <Input
    type="number"
    inputMode="numeric"
    value={configForm.trail_step_profit || ""}
    onWheel={(e) => e.currentTarget.blur()}
    onFocus={() => setEditing(true)}
    onChange={(e) =>
      setConfigForm({
        ...configForm,
        trail_step_profit: e.target.value,
      })
    }
  />

 <div className="flex gap-2 mt-1">
  {[10, 25, 50].map((pct) => (
    <Button
      key={pct}
      type="button"
      variant="outline"
      size="sm"
      onClick={() => {
        const capital = Number(configForm.capital_day_915 || 0);

        let effectiveMaxProfit =
          maxProfitMode === "pct"
            ? (Number(configForm.max_profit_abs || 0) / 100) * capital
            : Number(configForm.max_profit_abs || 0);

        if (!effectiveMaxProfit || effectiveMaxProfit <= 0) return;

        setConfigForm({
          ...configForm,
          trail_step_profit: Math.round(
            (pct / 100) * effectiveMaxProfit
          ),
        });
      }}
    >
      {pct}%
    </Button>
  ))}
</div>

</div>


                  <div className="space-y-2">
                    <Label>Max Trades Per Day</Label>
                    <Input
                      type="number"
                      value={configForm.max_trades_per_day || ''}
                      onChange={(e) => setConfigForm({...configForm, max_trades_per_day: e.target.value})}
                        onFocus={() => setEditing(true)}
                        
                    />
                  </div>
             <div className="space-y-2">
  <Label>Consecutive Loss Limit</Label>

  <Input
    type="number"
    inputMode="numeric"
    min={0}
    max={10}
    value={configForm.max_consecutive_losses || ""}
    onWheel={(e) => e.currentTarget.blur()}
    onFocus={() => setEditing(true)}
    onChange={(e) => {
      let val = Number(e.target.value);

      if (Number.isNaN(val)) return;

      if (val > 10) val = 10;
      if (val < 0) val = 0;

      setConfigForm({
        ...configForm,
        max_consecutive_losses: val,
      });
    }}
  />
</div>


                  <div className="space-y-2">
                    <Label>Cooldown Period (minutes)</Label>
                    <Input
                      type="number"
                      value={configForm.cooldown_min || ''}
                      onChange={(e) => setConfigForm({...configForm, cooldown_min: e.target.value})}
                        onFocus={() => setEditing(true)}
                
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Side Lock</Label>
                    <Select 
                      value={configForm.side_lock || "none"} 
                      onValueChange={(value) => setConfigForm({...configForm, side_lock: value === "none" ? null : value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="BUY">BUY Only</SelectItem>
                        <SelectItem value="SELL">SELL Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 flex items-end">
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={configForm.cooldown_on_profit === true}
                        onCheckedChange={(checked) => setConfigForm(prev => ({...prev, cooldown_on_profit: checked}))}
                      />
                      <Label>Enable Cooldown after Profitable Trade</Label>
                    </div>
                  </div>
                </div>
                <div className="mt-6 flex gap-3">
                  <Button onClick={handleUpdateConfig} className="bg-orange-500 hover:bg-orange-600">
                    Save All Configuration
                  </Button>
                  <Button onClick={() => fetchData()} variant="outline">
                    Refresh
                  </Button>
                </div>
              </CardContent>
            </Card>
                        </div>
                        )}
{activeSection === "tradebook" && (
  
<div className="mt-10">
  <div
    className="
      glass-card
      bg-card/60
      backdrop-blur-xl
      rounded-3xl
      shadow-2xl
      border border-border
      p-0
      min-h-[70vh] sm:min-h-0
      flex flex-col
    "
  >
    {/* SCROLL CONTAINER */}
    <div
      className="
        flex-1
        overflow-x-auto
        overflow-y-auto
        overscroll-contain
        touch-pan-x touch-pan-y 
        scrollbar-thin
      "
    >
      <div className="w-full max-w-full p-3 sm:p-4">

        <Tradebook trades={trades} />
      </div>
    </div>
  </div>
</div>
)}
        

          {activeSection === "logs" && (
  <div className="mt-10">
            <Card className="bg-card/60 text-card-foreground backdrop-blur-xl border border-border 
            shadow-2xl hover:shadow-orange-600/40 transition-all duration-300 min-h-[70vh] sm:min-h-0
    flex flex-col
">


              <CardHeader>
                <CardTitle>Activity Logs</CardTitle>
              </CardHeader>
              <CardContent className="flex-1 overflow-y-auto">
                <div className="space-y-2">

                  {logs.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No logs available</p>
                  ) : (
                    logs.map((log, idx) => (
                      <div
  key={idx}
  className="p-3 border-l-4 rounded bg-card border border-border"
  style={{
    borderLeftColor: log.level === "error" ? "#ef4444" : "#10b981",
  }}
>

                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-sm text-foreground">{log.message}</p>
                            <p className="text-xs text-muted-foreground mt-1">{log._ts
  ? new Date(log._ts).toLocaleString("en-IN")
  : "—"}
</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
                  </div>
)}
{activeSection === "journal" && (
  <div className="mt-10 flex min-h-[70vh] items-center justify-center px-4 text-center">
    <div className="max-w-sm">
      <h3 className="text-xl font-semibold text-foreground">
        Trade Journal
      </h3>
      <p className="mt-2 text-sm text-muted-foreground">
        Coming soon 🚧
      </p>
    </div>
  </div>

)}

        





</div>
</SwipeGuard>

{/* Kill Switch Confirmation Modal */}
{showKillSwitch && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
    <div className="bg-card border border-red-700 rounded-2xl p-6 w-full max-w-md">
      <h2 className="text-xl font-bold text-red-600 mb-3">
        ⚠️ Kill Switch
      </h2>

      <p className="text-sm text-muted-foreground mb-4">
        This will cancel all orders, square off all positions, and lock trading
        for the entire day.
        <br />
        <b>This action cannot be reversed.</b>
      </p>

      <Label className="text-foreground">
        Type <b>KILL NOW</b> to confirm
      </Label>

      <Input
        value={killText}
        onChange={(e) => setKillText(e.target.value)}
        className="mt-2 text-foreground"
        placeholder="KILL NOW"
      />

      <div className="flex justify-end gap-3 mt-6">
        <Button
         className="text-foreground"
          variant="outline"
          onClick={() => {
            setShowKillSwitch(false);
            setKillText("");
          }}
        >
          Cancel
        </Button>

        <Button
          className="bg-red-800 hover:bg-black text-white"
          disabled={killText !== "KILL NOW"}
          onClick={handleKillSwitch}
        >
          KILL SWITCH
        </Button>
      </div>
    </div>
  </div>
)}

{confirmAction && (
  <div
    className="fixed inset-0 z-40"
    onClick={() => setConfirmAction(null)}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      className="
        fixed z-50
        w-[min(92vw,18rem)]
        glass-card
        rounded-2xl
        border border-border
        shadow-2xl
        p-4
        flex flex-col gap-4
      "
     style={{
  left: "50%",
  top: "50%",
  transform: "translate(-50%, -50%)",
}}

    >
      <h3 className={`text-sm font-semibold ${confirmAction.color}`}>
        {confirmAction.title}
      </h3>

      <p className="text-xs text-muted-foreground">
        {confirmAction.message}
      </p>

      <div className="flex gap-2 mt-2">
        <Button
          variant="outline"
          className="flex-1"
          onClick={() => setConfirmAction(null)}
        >
          Cancel
        </Button>

        <Button
          className={`flex-1 ${confirmAction.btnClass}`}
          onClick={async () => {
            await confirmAction.onConfirm();
            setConfirmAction(null);
          }}
        >
          Confirm
        </Button>
      </div>
    </div>
  </div>
)}

{isHeaderCollapsed && (
<button
  onClick={() => setShowEmergency(v => !v)}
  className="
    fixed bottom-6 left-6 z-50
    w-14 h-14 rounded-full
    bg-orange-500 text-white
    shadow-xl shadow-orange-500/40
    flex items-center justify-center
    hover:scale-105 active:scale-95
    transition
    animate-emergency-flyin
  "
  aria-label="Emergency Controls"
>
  {showEmergency ? (
    "✕"
  ) : fabIcon === "app" ? (
    <img
      src="/boho-mark-orange.png"
      alt="Boho"
      className={`h-12 w-12 select-none ${
        isSpinning ? "rotate-180" : "rotate-0"
      }`}
      style={{ transition: "transform 0.7s ease-in-out" }}
    />
  ) : (
    <Siren
      className={`h-8 w-8 ${
        isSpinning ? "rotate-180" : "rotate-0"
      }`}
      style={{ transition: "transform 0.7s ease-in-out" }}
    />
  )}
</button>
)}
  {showEmergency && (
  <div
    className="fixed inset-0 z-40"
    onClick={() => setShowEmergency(false)}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      className="
        fixed z-50
        w-[min(92vw,18rem)]
        max-h-[70vh]
        overflow-y-auto

        glass-card
        rounded-2xl
        border border-border
        shadow-2xl
        p-3
        flex flex-col gap-2
      "
   style={{
  left: "50%",
  top: "50%",
  transform: "translate(-50%, -50%)",
}}

    >
      {/* Reset Day */}
      <button
        onClick={() => {
          setShowEmergency(false);
          confirmResetDay();
        }}
        className="flex items-center gap-3 px-4 py-3 rounded-xl
          bg-card/70 hover:bg-blue-600/10 transition"
      >
        <RotateCcw size={16} className="text-blue-500" />
        <span className="text-sm font-medium">Reset Day</span>
      </button>

      {/* Cancel Orders */}
      <button
        onClick={() => {
          setShowEmergency(false);
          confirmCancelOrders();
        }}
        className="flex items-center gap-3 px-4 py-3 rounded-xl
          bg-card/70 hover:bg-yellow-600/10 transition"
      >
        <XCircle size={16} className="text-yellow-500" />
        <span className="text-sm font-medium">Cancel Orders</span>
      </button>

      {/* Square Off Positions */}
      <button
        onClick={() => {
          setShowEmergency(false);
          confirmKillPositions();
        }}
        className="flex items-center gap-3 px-4 py-3 rounded-xl
          bg-card/70 hover:bg-red-600/10 transition"
      >
        <Square size={16} className="text-red-500" />
        <span className="text-sm font-medium">Square Off Positions</span>
      </button>

      {/* Kill Switch */}
      <button
        onClick={() => {
          setShowEmergency(false);
          setShowKillSwitch(true);
        }}
        className="
          mt-2 px-4 py-3 rounded-xl
          bg-orange-500 text-white font-semibold
          hover:bg-orange-600 transition
        "
      >
        🚨 Kill Switch
      </button>
    </div>
  </div>
)}
</div>
 



  );
};

export default Dashboard;
